import { useState, useCallback, useRef } from 'react';
import api from '../api';

export function useConversations() {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    // Track current filter scope so optimistic updates and deletes stay consistent
    const currentScopeRef = useRef({ genieId: null, clientName: null });

    // Load conversations from backend filtered by genie AND client.
    // Both are required to properly isolate history per user > client > genie.
    // Returns early (no-op) if genieId is falsy — avoids fetching all conversations
    // when the selected genie has not yet been determined by GenieContext.
    const loadConversations = useCallback(async (genieId = null, clientName = null) => {
        // Guard: do not fetch if genieId is absent — we'd get unscoped results
        // that would mix history across genies / clients.
        if (!genieId) {
            console.log('[CONVERSATIONS] loadConversations skipped — genieId not yet available');
            return;
        }

        // Record current scope so createConversation / deleteConversation can stay aligned
        currentScopeRef.current = { genieId, clientName };

        try {
            setLoading(true);
            const params = { limit: 10, genieId };
            if (clientName) {
                params.clientName = clientName;
            }
            const response = await api.get('/api/conversations', { params });
            setConversations(response.data.conversations || []);
        } catch (error) {
            console.error('Failed to load conversations:', error);
            // Do NOT wipe the existing conversation list on a transient error.
            // Clearing to [] here was the original bug that caused the sidebar to
            // appear empty after a URL refresh or re-login if the first
            // /api/conversations request failed (e.g. token not yet refreshed,
            // brief network hiccup, or server restart).  Preserving the previous
            // state means users still see their history from the last successful
            // fetch instead of a blank panel.
            // The list will be updated on the next successful call (e.g. when the
            // user sends a new message or the effect re-fires after auth completes).
        } finally {
            setLoading(false);
        }
    }, []);

    // Load a specific conversation with all messages
    const loadConversation = useCallback(async (conversationId) => {
        try {
            const response = await api.get(`/api/conversations/${conversationId}`);
            return response.data;
        } catch (error) {
            console.error('Failed to load conversation:', error);
            return null;
        }
    }, []);

    const createConversation = useCallback(async (genieId, genieName, title, databricksConversationId, databricksSpaceId, clientName = null, spnName = null) => {
        try {
            const response = await api.post('/api/conversations', {
                genieId,
                genieName,
                title,
                databricksConversationId,
                databricksSpaceId,
                clientName,
                spnName
            });
            
            // Add to local state only if this conversation belongs to the currently
            // displayed scope (same genieId + clientName). Skipping this guard would
            // cause a conversation from a different client to briefly appear in the
            // sidebar before the next loadConversations re-scopes the list.
            const newConv = response.data.conversation;
            const scope = currentScopeRef.current;
            if (scope.genieId === genieId && scope.clientName === (clientName || null)) {
                setConversations(prev => [newConv, ...prev.slice(0, 9)]); // Keep only 10 most recent
            }
            
            return newConv;
        } catch (error) {
            console.error('Failed to create conversation:', error);
            return null;
        }
    }, []);
    
    // Update a conversation's last-activity timestamp so the sidebar time is accurate.
    // Called after each successful AI response in an existing conversation.
    // Uses fire-and-forget pattern — failures are logged but don't block the UI.
    const updateConversationActivity = useCallback(async (conversationId) => {
        if (!conversationId) return;
        try {
            const response = await api.patch(`/api/conversations/${conversationId}/activity`);
            // Optimistically update the local conversations list so the sidebar
            // reflects the new timestamp without waiting for a full reload.
            const newUpdatedAt = response.data?.updated_at;
            if (newUpdatedAt) {
                setConversations(prev =>
                    prev.map(c => c.id === conversationId ? { ...c, updated_at: newUpdatedAt } : c)
                );
            }
        } catch (error) {
            console.error('Failed to update conversation activity:', error);
        }
    }, []);
    
    // Delete a conversation and refresh the scoped list
    const deleteConversation = useCallback(async (conversationId) => {
        try {
            await api.delete(`/api/conversations/${conversationId}`);
            
            // Refresh with the same scope that was used when the list was last loaded.
            // Previously called loadConversations() with no args, which fetched all
            // conversations across all genies/clients — wrong scoping.
            const { genieId, clientName } = currentScopeRef.current;
            await loadConversations(genieId, clientName);
            
            return true;
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            return false;
        }
    }, [loadConversations]);

    // Save (overwrite) agent conversation messages to the DB.
    // Agent conversations store messages locally (no Databricks thread).
    // Fire-and-forget: failures are logged but don't block the UI.
    const saveAgentMessages = useCallback(async (conversationId, messages) => {
        if (!conversationId || !Array.isArray(messages)) return;
        try {
            await api.patch(`/api/conversations/${conversationId}/messages`, { messages });
        } catch (error) {
            console.error('[CONVERSATIONS] Failed to save agent messages:', error);
        }
    }, []);
    
    return {
        conversations,
        loading,
        loadConversations,
        loadConversation,
        createConversation,
        updateConversationActivity,
        saveAgentMessages,
        deleteConversation
    };
}